import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Cpu, Satellite, BarChart3, Cloud, Leaf, Calendar, Target, ChevronDown, ArrowRight } from 'lucide-react';
import Impressum from './Impressum'; // Add this import

const Prognosen = () => {
  const [scrolled, setScrolled] = useState(false);
  const [currentLang, setCurrentLang] = useState('de');
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [showImpressum, setShowImpressum] = useState(false); // Add this state

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const translations = {
    de: {
      nav: {
        home: "Startseite",
        demo: "Demo anfordern"
      },
      hero: {
        title: "KI-gestützte Prognosen",
        subtitle: "93% Genauigkeit bei Ernte- und Preisprognosen durch Machine Learning",
        description: "Nutzen Sie die Kraft von Künstlicher Intelligenz und Satellitendaten für präzise Vorhersagen"
      },
      features: {
        title: "Unsere Prognoseleistungen",
        items: [
          {
            title: "Ertragsvorhersagen",
            desc: "Präzise Schätzungen Ihrer Ernteerträge basierend auf aktuellen Felddaten",
            icon: "yield"
          },
          {
            title: "Preisprognosen",
            desc: "Marktpreisentwicklungen für optimale Verkaufszeitpunkte",
            icon: "price"
          },
          {
            title: "Wetteranalysen",
            desc: "Detaillierte Wettervorhersagen für Ihre spezifischen Feldstandorte",
            icon: "weather"
          },
          {
            title: "Risikofrüherkennung",
            desc: "Identifikation von Krankheiten und Schädlingen bevor sie sich ausbreiten",
            icon: "risk"
          }
        ]
      },
      technology: {
        title: "Unsere Technologie",
        subtitle: "Modernste KI trifft auf bewährte Agrardaten",
        items: [
          {
            title: "Satellitendaten",
            desc: "Tägliche Aufnahmen von Sentinel und anderen Erdbeobachtungssatelliten"
          },
          {
            title: "Machine Learning",
            desc: "Neuronale Netze trainiert mit Millionen von Datenpunkten"
          },
          {
            title: "Historische Daten",
            desc: "20+ Jahre Ernte- und Wetterdaten für präzise Modelle"
          },
          {
            title: "Echtzeitanalyse",
            desc: "Kontinuierliche Aktualisierung der Prognosen mit neuen Daten"
          }
        ]
      },
      accuracy: {
        title: "Nachgewiesene Genauigkeit",
        subtitle: "Unsere Prognosen im Vergleich",
        stats: [
          { label: "Ertragsvorhersage", value: "93%", desc: "Genauigkeit 30 Tage vor Ernte" },
          { label: "Preisvorhersage", value: "87%", desc: "Trefferquote bei 7-Tage-Prognose" },
          { label: "Wettervorhersage", value: "95%", desc: "Genauigkeit für 5-Tage-Vorhersage" },
          { label: "Schädlingserkennung", value: "89%", desc: "Früherkennung vor sichtbarem Befall" }
        ]
      },
      process: {
        title: "So funktionieren unsere Prognosen",
        steps: [
          {
            title: "Datenerfassung",
            desc: "Satellitendaten, Wetterstationen und historische Daten werden gesammelt"
          },
          {
            title: "KI-Analyse",
            desc: "Machine Learning Modelle verarbeiten und analysieren die Daten"
          },
          {
            title: "Validierung",
            desc: "Ergebnisse werden mit Referenzdaten abgeglichen und validiert"
          },
          {
            title: "Bereitstellung",
            desc: "Prognosen werden in Echtzeit auf Ihrer Plattform bereitgestellt"
          }
        ]
      },
      benefits: {
        title: "Ihre Vorteile",
        items: [
          "Optimale Erntezeitpunkte bestimmen",
          "Bessere Vermarktungsentscheidungen treffen",
          "Risiken frühzeitig erkennen und vermeiden",
          "Ressourcen effizienter einsetzen",
          "Erträge maximieren durch datenbasierte Entscheidungen"
        ]
      },
      cta: {
        title: "Nutzen Sie die Kraft der KI für Ihren Erfolg",
        subtitle: "Starten Sie noch heute mit präzisen Prognosen für Ihren Betrieb",
        button: "Kostenlose Demo anfordern"
      }
    },
    en: {
      nav: {
        home: "Home",
        demo: "Request Demo"
      },
      hero: {
        title: "AI-Powered Forecasts",
        subtitle: "93% accuracy in harvest and price predictions through Machine Learning",
        description: "Harness the power of Artificial Intelligence and satellite data for precise predictions"
      },
      features: {
        title: "Our Forecasting Services",
        items: [
          {
            title: "Yield Predictions",
            desc: "Precise estimates of your harvest yields based on current field data",
            icon: "yield"
          },
          {
            title: "Price Forecasts",
            desc: "Market price developments for optimal selling times",
            icon: "price"
          },
          {
            title: "Weather Analysis",
            desc: "Detailed weather forecasts for your specific field locations",
            icon: "weather"
          },
          {
            title: "Early Risk Detection",
            desc: "Identification of diseases and pests before they spread",
            icon: "risk"
          }
        ]
      },
      technology: {
        title: "Our Technology",
        subtitle: "State-of-the-art AI meets proven agricultural data",
        items: [
          {
            title: "Satellite Data",
            desc: "Daily imagery from Sentinel and other Earth observation satellites"
          },
          {
            title: "Machine Learning",
            desc: "Neural networks trained with millions of data points"
          },
          {
            title: "Historical Data",
            desc: "20+ years of harvest and weather data for precise models"
          },
          {
            title: "Real-time Analysis",
            desc: "Continuous updating of forecasts with new data"
          }
        ]
      },
      accuracy: {
        title: "Proven Accuracy",
        subtitle: "Our forecasts in comparison",
        stats: [
          { label: "Yield Prediction", value: "93%", desc: "Accuracy 30 days before harvest" },
          { label: "Price Forecast", value: "87%", desc: "Hit rate for 7-day forecast" },
          { label: "Weather Forecast", value: "95%", desc: "Accuracy for 5-day forecast" },
          { label: "Pest Detection", value: "89%", desc: "Early detection before visible infestation" }
        ]
      },
      process: {
        title: "How our forecasts work",
        steps: [
          {
            title: "Data Collection",
            desc: "Satellite data, weather stations and historical data are collected"
          },
          {
            title: "AI Analysis",
            desc: "Machine learning models process and analyze the data"
          },
          {
            title: "Validation",
            desc: "Results are compared and validated with reference data"
          },
          {
            title: "Delivery",
            desc: "Forecasts are provided in real-time on your platform"
          }
        ]
      },
      benefits: {
        title: "Your Benefits",
        items: [
          "Determine optimal harvest times",
          "Make better marketing decisions",
          "Detect and avoid risks early",
          "Use resources more efficiently",
          "Maximize yields through data-driven decisions"
        ]
      },
      cta: {
        title: "Harness the power of AI for your success",
        subtitle: "Start today with precise forecasts for your farm",
        button: "Request free demo"
      }
    },
    it: {
      nav: {
        home: "Home",
        demo: "Richiedi Demo"
      },
      hero: {
        title: "Previsioni basate su IA",
        subtitle: "93% di precisione nelle previsioni di raccolto e prezzi attraverso Machine Learning",
        description: "Sfrutta la potenza dell'Intelligenza Artificiale e dei dati satellitari per previsioni precise"
      },
      features: {
        title: "I Nostri Servizi di Previsione",
        items: [
          {
            title: "Previsioni di Resa",
            desc: "Stime precise dei tuoi raccolti basate sui dati attuali del campo",
            icon: "yield"
          },
          {
            title: "Previsioni dei Prezzi",
            desc: "Sviluppi dei prezzi di mercato per tempi di vendita ottimali",
            icon: "price"
          },
          {
            title: "Analisi Meteo",
            desc: "Previsioni meteo dettagliate per le tue specifiche posizioni di campo",
            icon: "weather"
          },
          {
            title: "Rilevamento Precoce dei Rischi",
            desc: "Identificazione di malattie e parassiti prima che si diffondano",
            icon: "risk"
          }
        ]
      },
      technology: {
        title: "La Nostra Tecnologia",
        subtitle: "IA all'avanguardia incontra dati agricoli comprovati",
        items: [
          {
            title: "Dati Satellitari",
            desc: "Immagini giornaliere da Sentinel e altri satelliti di osservazione terrestre"
          },
          {
            title: "Machine Learning",
            desc: "Reti neurali addestrate con milioni di punti dati"
          },
          {
            title: "Dati Storici",
            desc: "20+ anni di dati su raccolti e meteo per modelli precisi"
          },
          {
            title: "Analisi in Tempo Reale",
            desc: "Aggiornamento continuo delle previsioni con nuovi dati"
          }
        ]
      },
      accuracy: {
        title: "Precisione Comprovata",
        subtitle: "Le nostre previsioni a confronto",
        stats: [
          { label: "Previsione Resa", value: "93%", desc: "Precisione 30 giorni prima del raccolto" },
          { label: "Previsione Prezzi", value: "87%", desc: "Tasso di successo per previsione a 7 giorni" },
          { label: "Previsione Meteo", value: "95%", desc: "Precisione per previsione a 5 giorni" },
          { label: "Rilevamento Parassiti", value: "89%", desc: "Rilevamento precoce prima dell'infestazione visibile" }
        ]
      },
      process: {
        title: "Come funzionano le nostre previsioni",
        steps: [
          {
            title: "Raccolta Dati",
            desc: "Vengono raccolti dati satellitari, stazioni meteo e dati storici"
          },
          {
            title: "Analisi IA",
            desc: "I modelli di machine learning elaborano e analizzano i dati"
          },
          {
            title: "Validazione",
            desc: "I risultati vengono confrontati e validati con dati di riferimento"
          },
          {
            title: "Consegna",
            desc: "Le previsioni sono fornite in tempo reale sulla tua piattaforma"
          }
        ]
      },
      benefits: {
        title: "I Tuoi Vantaggi",
        items: [
          "Determinare i tempi ottimali di raccolta",
          "Prendere decisioni di marketing migliori",
          "Rilevare ed evitare rischi precocemente",
          "Utilizzare le risorse in modo più efficiente",
          "Massimizzare i rendimenti attraverso decisioni basate sui dati"
        ]
      },
      cta: {
        title: "Sfrutta la potenza dell'IA per il tuo successo",
        subtitle: "Inizia oggi con previsioni precise per la tua azienda",
        button: "Richiedi demo gratuita"
      }
    },
    es: {
      nav: {
        home: "Inicio",
        demo: "Solicitar Demo"
      },
      hero: {
        title: "Pronósticos impulsados por IA",
        subtitle: "93% de precisión en predicciones de cosecha y precios mediante Machine Learning",
        description: "Aproveche el poder de la Inteligencia Artificial y datos satelitales para predicciones precisas"
      },
      features: {
        title: "Nuestros Servicios de Pronóstico",
        items: [
          {
            title: "Predicciones de Rendimiento",
            desc: "Estimaciones precisas de sus rendimientos de cosecha basadas en datos actuales del campo",
            icon: "yield"
          },
          {
            title: "Pronósticos de Precios",
            desc: "Desarrollos de precios del mercado para tiempos óptimos de venta",
            icon: "price"
          },
          {
            title: "Análisis del Clima",
            desc: "Pronósticos meteorológicos detallados para sus ubicaciones específicas de campo",
            icon: "weather"
          },
          {
            title: "Detección Temprana de Riesgos",
            desc: "Identificación de enfermedades y plagas antes de que se propaguen",
            icon: "risk"
          }
        ]
      },
      technology: {
        title: "Nuestra Tecnología",
        subtitle: "IA de vanguardia se encuentra con datos agrícolas probados",
        items: [
          {
            title: "Datos Satelitales",
            desc: "Imágenes diarias de Sentinel y otros satélites de observación terrestre"
          },
          {
            title: "Machine Learning",
            desc: "Redes neuronales entrenadas con millones de puntos de datos"
          },
          {
            title: "Datos Históricos",
            desc: "20+ años de datos de cosecha y clima para modelos precisos"
          },
          {
            title: "Análisis en Tiempo Real",
            desc: "Actualización continua de pronósticos con nuevos datos"
          }
        ]
      },
      accuracy: {
        title: "Precisión Comprobada",
        subtitle: "Nuestros pronósticos en comparación",
        stats: [
          { label: "Predicción de Rendimiento", value: "93%", desc: "Precisión 30 días antes de la cosecha" },
          { label: "Pronóstico de Precios", value: "87%", desc: "Tasa de acierto para pronóstico de 7 días" },
          { label: "Pronóstico del Clima", value: "95%", desc: "Precisión para pronóstico de 5 días" },
          { label: "Detección de Plagas", value: "89%", desc: "Detección temprana antes de infestación visible" }
        ]
      },
      process: {
        title: "Cómo funcionan nuestros pronósticos",
        steps: [
          {
            title: "Recolección de Datos",
            desc: "Se recopilan datos satelitales, estaciones meteorológicas y datos históricos"
          },
          {
            title: "Análisis de IA",
            desc: "Los modelos de aprendizaje automático procesan y analizan los datos"
          },
          {
            title: "Validación",
            desc: "Los resultados se comparan y validan con datos de referencia"
          },
          {
            title: "Entrega",
            desc: "Los pronósticos se proporcionan en tiempo real en su plataforma"
          }
        ]
      },
      benefits: {
        title: "Tus Beneficios",
        items: [
          "Determinar tiempos óptimos de cosecha",
          "Tomar mejores decisiones de marketing",
          "Detectar y evitar riesgos temprano",
          "Usar recursos más eficientemente",
          "Maximizar rendimientos a través de decisiones basadas en datos"
        ]
      },
      cta: {
        title: "Aproveche el poder de la IA para su éxito",
        subtitle: "Comience hoy con pronósticos precisos para su granja",
        button: "Solicitar demo gratuita"
      }
    }
  };

  const t = translations[currentLang];

  const languages = [
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  const ZebraLogo = () => (
    <img 
      src="/logo.png" 
      alt="Lazzaro Logo" 
      className="w-full h-full object-contain"
    />
  );

  const getFeatureIcon = (iconType) => {
    switch(iconType) {
      case 'yield': return <Leaf className="w-8 h-8" />;
      case 'price': return <TrendingUp className="w-8 h-8" />;
      case 'weather': return <Cloud className="w-8 h-8" />;
      case 'risk': return <Target className="w-8 h-8" />;
      default: return <Cpu className="w-8 h-8" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-gray-900/95 backdrop-blur-xl shadow-2xl' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-12 h-12 relative">
                <ZebraLogo />
              </div>
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-amber-500 bg-clip-text text-transparent">Lazzaro1</span>
                <p className="text-xs text-amber-400/80 font-medium">SMART DATA FOR SMARTER FARMING</p>
              </div>
            </Link>
            
            <div className="flex items-center space-x-8">
              <Link to="/" className="hover:text-cyan-400 transition-colors">{t.nav.home}</Link>
              
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center space-x-2 hover:text-cyan-400 transition-colors"
                >
                  <span>{languages.find(l => l.code === currentLang)?.flag}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {langMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-gray-900 border border-gray-700 rounded-lg shadow-xl">
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setCurrentLang(lang.code);
                          setLangMenuOpen(false);
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-800 transition-colors flex items-center space-x-2 ${
                          currentLang === lang.code ? 'text-cyan-400' : ''
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <button className="bg-gradient-to-r from-cyan-500 to-amber-600 px-6 py-2 rounded-full hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 transform hover:-translate-y-0.5">
                {t.nav.demo}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-orange-900/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white mb-6">
              <TrendingUp className="w-16 h-16" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
              {t.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-4 max-w-3xl mx-auto">
              {t.hero.subtitle}
            </p>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {t.hero.description}
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.features.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.features.items.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-gray-800/50 hover:bg-gray-800 transition-all duration-300">
                <div className="inline-flex p-3 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-600/20 text-amber-400 mb-4">
                  {getFeatureIcon(feature.icon)}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t.technology.title}</h2>
            <p className="text-xl text-gray-400">{t.technology.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.technology.items.map((tech, index) => (
              <div key={index} className="p-6 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900">
                <div className="mb-4">
                  {index === 0 && <Satellite className="w-10 h-10 text-amber-400" />}
                  {index === 1 && <Cpu className="w-10 h-10 text-amber-400" />}
                  {index === 2 && <Calendar className="w-10 h-10 text-amber-400" />}
                  {index === 3 && <BarChart3 className="w-10 h-10 text-amber-400" />}
                </div>
                <h3 className="text-xl font-semibold mb-2">{tech.title}</h3>
                <p className="text-gray-400">{tech.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accuracy Stats */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t.accuracy.title}</h2>
            <p className="text-xl text-gray-400">{t.accuracy.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.accuracy.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <h3 className="text-xl font-semibold mb-2">{stat.label}</h3>
                <p className="text-gray-400 text-sm">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.process.title}</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {t.process.steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-400">{step.desc}</p>
                </div>
                {index < t.process.steps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-8 -right-4 w-8 h-8 text-gray-600" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.benefits.title}</h2>
          <div className="max-w-3xl mx-auto">
            <div className="grid md:grid-cols-2 gap-4">
              {t.benefits.items.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3 p-4 rounded-lg bg-gray-800/50">
                  <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                  <p className="text-lg">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 to-orange-900/20"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t.cta.title}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t.cta.subtitle}
          </p>
          <button className="group bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-4 rounded-full text-lg font-semibold hover:shadow-2xl hover:shadow-amber-500/25 transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center mx-auto">
            {t.cta.button}
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer - modified to add Impressum */}
      <footer className="bg-gray-900 py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center space-x-3 mb-4">
              <div className="w-10 h-10">
                <ZebraLogo />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-amber-500 bg-clip-text text-transparent">Lazzaro1</span>
            </Link>
            <p className="text-gray-400">© 2024 Lazzaro One. Alle Rechte vorbehalten.</p>
            
            {/* Add Impressum Link */}
            <div className="mt-4">
              <button
                onClick={() => setShowImpressum(true)}
                className="text-gray-400 hover:text-cyan-400 transition-colors text-sm"
              >
                Impressum
              </button>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Add Impressum Modal */}
      <Impressum isOpen={showImpressum} onClose={() => setShowImpressum(false)} />
    </div>
  );
};

export default Prognosen;